import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { procurementApi } from 'api/procurement';
import { ViewEntityHeader } from 'components/entityViewer/ViewEntityHeader';
import { ViewEntityPage } from 'components/entityViewer/ViewEntityPage';
import { usePurchaseOrderActions } from 'components/procurement/components/purchaseOrdersV2/hooks/usePurchaseOrderActions';
import { useCountriesQuery } from 'components/procurement/components/purchaseOrdersV2/queries/AddressesQueries';
import {
    purchaseOrderQueryKeys,
    usePurchaseOrderQuery,
    usePurchaseOrdersV2Search,
} from 'components/procurement/components/purchaseOrdersV2/queries/PurchaseOrderQueries';
import { ReasonCodeDialog } from 'components/procurement/components/purchaseOrdersV2/ReasonCodeDialog';
import {
    buildPurchaseOrderAddItemPayloadRow,
    buildPurchaseOrderItemRowFromMaster,
    buildPurchaseOrderMapItemDiff,
    buildPurchaseOrderPrimaryDraft,
    buildPurchaseOrderPrimaryUpdatePayload,
    formatDuplicateLineItemLiveSummary,
    getDefaultCompanySourceIdsForPurchaseOrderItem,
    getEligibleCompanySourceIdsForPurchaseOrderItem,
    getItemEditInputValue,
    getPoMapId,
    hasInvalidEmptyCompanySourceEligibility,
    reconcileSelectedCompanySourceIdsForPurchaseOrderItem,
    validateDuplicateLineItems,
    validatePurchaseOrderEdit,
} from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderCreateAndEditUtils';
import { Button } from 'shadcn/components/ui/Button';
import { useItemMasterQuery } from 'shared/hooks/warehouse/useItemMasterQuery';

import { PurchaseOrderEditPrimaryCard } from './PurchaseOrderEditPrimaryCard';
import { PurchaseOrderHeaderContent } from './PurchaseOrderHeaderContent';
import { PurchaseOrderItemInfoCard } from './PurchaseOrderItemInfoCard';
import { PurchaseOrderPrimaryInfoCard } from './PurchaseOrderPrimaryInfoCard';

/**
 * Displays detailed information for a single purchase order.
 *
 * Fetches purchase order data via API using the purchase order ID from route params.
 * Renders a header plus primary information card and item information sections.
 *
 * @returns {JSX.Element} Purchase order entity view.
 */
export function ViewPurchaseOrderEntity() {
    const { purchaseOrderId } = useParams();
    const location = useLocation();
    const history = useHistory();
    const queryClient = useQueryClient();

    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

    // Use search API to fetch purchase order by ID (using poNumber filter)
    const {
        data: searchResult,
        error,
        isError,
        isFetching,
        isLoading,
        refetch,
    } = usePurchaseOrdersV2Search(
        { poNumber: purchaseOrderId },
        {
            enabled: Boolean(purchaseOrderId),
            refetchOnWindowFocus: !isEditing,
            refetchOnReconnect: !isEditing,
        },
    );

    const {
        cancelDialog,
        cancelState,
        canSendToVendor,
        confirmCancel,
        downloadCsv,
        downloadPdf,
        downloadShipList,
        sendToVendor,
    } = usePurchaseOrderActions();

    // Extract the purchase order from search results (should be first item in rows array)
    // NOTE: This "search row" shape is what our primary/header components expect (facilityName, vendorName, etc.)
    const purchaseOrder = searchResult?.rows?.[0] ?? null;

    // PO detail includes items (used for inline item edits + bulk save diffing)
    const { data: purchaseOrderDetail } = usePurchaseOrderQuery({
        id: purchaseOrderId,
        enabled: Boolean(purchaseOrderId),
        options: {
            refetchOnWindowFocus: !isEditing,
            refetchOnReconnect: !isEditing,
        },
    });
    // Keep primary/header bound to the search-row PO to avoid field-shape mismatches.
    const primaryPurchaseOrder = purchaseOrder ?? null;

    const { data: countries = [] } = useCountriesQuery({ enabled: true });

    // Query for item masters (for adding items in edit mode)
    const { data: itemMasters = [] } = useItemMasterQuery({
        activeOnly: true,
        enabled: isEditing,
        mmOnly: true,
    });

    const itemMastersBySku = useMemo(() => {
        const map = new Map();
        for (const row of itemMasters) {
            map.set(String(row.sku), row);
        }
        return map;
    }, [itemMasters]);

    const [primaryDraft, setPrimaryDraft] = useState(null);
    const [removedItemIds, setRemovedItemIds] = useState(() => new Set());
    const [qtyReasonCode, setQtyReasonCode] = useState('');
    const [removeReasonCode, setRemoveReasonCode] = useState('');
    const [itemEditsVersion, setItemEditsVersion] = useState(0);

    // State for adding new items in edit mode
    const [addedItems, setAddedItems] = useState([]);
    const [selectedExternalSku, setSelectedExternalSku] = useState('');
    const [selectedItemMasterSku, setSelectedItemMasterSku] = useState('');
    const [selectedCompanySourceIds, setSelectedCompanySourceIds] = useState([]);
    const [addItemReasonCode, setAddItemReasonCode] = useState('');
    const [isAddingItem, setIsAddingItem] = useState(false);
    const [addItemError, setAddItemError] = useState('');
    const nextIdRef = useRef(1);

    // Uncontrolled item edits are read from refs at save time.
    // inputs[rowId][field] = HTMLInputElement
    // selects[rowId][field] = string
    // multi[rowId][field] = string[]
    const itemEditStateRef = useRef({
        inputs: {},
        selects: {},
        multi: {},
    });

    const syncDuplicateLineItemError = useCallback(() => {
        const persistedItems = purchaseOrderDetail?.items ?? purchaseOrderDetail?.purchaseOrderItems ?? [];
        const activeRows = [
            ...(Array.isArray(persistedItems) ? persistedItems : []).filter((item) => {
                const poMapId = getPoMapId(item);
                return poMapId && !removedItemIds.has(poMapId);
            }),
            ...addedItems.filter((item) => !removedItemIds.has(String(item.id))),
        ];
        const { itemSummaryErrors: duplicateSummaryErrors } = validateDuplicateLineItems({
            rows: activeRows,
            itemEditStateRef,
            itemMastersBySku,
            removedItemIds: new Set(),
        });
        setAddItemError(formatDuplicateLineItemLiveSummary(duplicateSummaryErrors));
    }, [addedItems, itemEditStateRef, itemMastersBySku, purchaseOrderDetail, removedItemIds]);

    useEffect(() => {
        syncDuplicateLineItemError();
    }, [syncDuplicateLineItemError]);

    const handleItemEditsChange = useCallback(() => {
        syncDuplicateLineItemError();
        setItemEditsVersion((v) => v + 1);
    }, [syncDuplicateLineItemError]);

    // Get and maintain filters from the purchase orders table for back navigation
    const purchaseOrdersFiltersFromState =
        typeof location.state?.purchaseOrdersFilters === 'string' && location.state.purchaseOrdersFilters.length > 0
            ? location.state.purchaseOrdersFilters
            : '';
    const purchaseOrdersFilters =
        purchaseOrdersFiltersFromState ||
        (typeof location?.search === 'string' && location.search ? location.search : '') ||
        (typeof window !== 'undefined' && window?.location?.search ? window.location.search : '') ||
        '';
    const backHref = purchaseOrdersFilters
        ? { pathname: '/procurement/viewPurchaseOrder-v2', search: purchaseOrdersFilters }
        : '/procurement/viewPurchaseOrder-v2';

    const handleEditPurchaseOrder = useCallback(() => {
        if (!primaryPurchaseOrder) return;
        setIsEditing(true);
        setHasAttemptedSave(false);
        setPrimaryDraft(buildPurchaseOrderPrimaryDraft(primaryPurchaseOrder));
        setRemovedItemIds(new Set());
        setQtyReasonCode('');
        setRemoveReasonCode('');
        setAddedItems([]);
        setSelectedExternalSku('');
        setSelectedItemMasterSku('');
        setSelectedCompanySourceIds([]);
        setAddItemReasonCode('');
        setAddItemError('');
        itemEditStateRef.current = { inputs: {}, selects: {}, multi: {} };
    }, [primaryPurchaseOrder]);

    const handleDuplicatePurchaseOrder = useCallback(() => {
        const sourceId = purchaseOrderId ?? primaryPurchaseOrder?.id;
        history.push({
            pathname: '/procurement/createPurchaseOrder-v2',
            search: sourceId ? `?duplicateFrom=${encodeURIComponent(sourceId)}` : '',
            state: { purchaseOrdersFilters },
        });
    }, [history, primaryPurchaseOrder?.id, purchaseOrderId, purchaseOrdersFilters]);

    const handleOpenCancelPurchaseOrder = useCallback(() => {
        if (!primaryPurchaseOrder) return;
        cancelDialog.open(primaryPurchaseOrder);
    }, [cancelDialog, primaryPurchaseOrder]);

    const handleDownloadCsv = useCallback(() => {
        if (!primaryPurchaseOrder) return;
        downloadCsv(primaryPurchaseOrder);
    }, [downloadCsv, primaryPurchaseOrder]);

    const handleDownloadPdf = useCallback(() => {
        if (!primaryPurchaseOrder) return;
        downloadPdf(primaryPurchaseOrder);
    }, [downloadPdf, primaryPurchaseOrder]);

    const handleDownloadShipList = useCallback(() => {
        if (!primaryPurchaseOrder) return;
        downloadShipList(primaryPurchaseOrder);
    }, [downloadShipList, primaryPurchaseOrder]);

    const handleSendToVendor = useCallback(async () => {
        if (!primaryPurchaseOrder) return;
        try {
            const response = await sendToVendor(primaryPurchaseOrder, searchResult?.deptLeadCheck ?? false);
            if (!response?.status) {
                const message = response?.msg ? `Failed to send PO. ${response.msg}` : 'Failed to send PO.';
                toast.error(message);
                return;
            }
            toast.success('Sent PO to Vendor.');
            await refetch();
        } catch (err) {
            toast.error(`Failed to send PO. ${err?.message ?? ''}`.trim());
        }
    }, [primaryPurchaseOrder, refetch, searchResult?.deptLeadCheck, sendToVendor]);

    const isSendToVendorDisabled = useMemo(
        () => !canSendToVendor(primaryPurchaseOrder, searchResult?.deptLeadCheck ?? false),
        [canSendToVendor, primaryPurchaseOrder, searchResult?.deptLeadCheck],
    );

    const handleConfirmCancelPurchaseOrder = useCallback(
        async (reasonCode) => {
            try {
                const response = await confirmCancel(reasonCode);
                if (!response?.status) {
                    const message = response?.msg ? `Unable to cancel PO. ${response.msg}` : 'Unable to cancel PO.';
                    toast.error(message);
                    return;
                }
                cancelDialog.close();
                await refetch();
                await queryClient.invalidateQueries({ queryKey: purchaseOrderQueryKeys.detail(purchaseOrderId) });
                toast.success('Purchase Order cancelled');
            } catch (err) {
                toast.error(err?.message ?? 'Unable to cancel PO.');
            }
        },
        [cancelDialog, confirmCancel, queryClient, purchaseOrderId, refetch],
    );

    const handleCancelEditPurchaseOrder = useCallback(() => {
        setIsEditing(false);
        setHasAttemptedSave(false);
        setPrimaryDraft(null);
        setRemovedItemIds(new Set());
        setQtyReasonCode('');
        setRemoveReasonCode('');
        setAddedItems([]);
        setSelectedExternalSku('');
        setSelectedItemMasterSku('');
        setSelectedCompanySourceIds([]);
        setAddItemReasonCode('');
        setAddItemError('');
        itemEditStateRef.current = { inputs: {}, selects: {}, multi: {} };
    }, []);

    const selectedItemMaster = selectedItemMasterSku ? itemMastersBySku.get(String(selectedItemMasterSku)) : undefined;
    const eligibleSourceIds = useMemo(
        () => getEligibleCompanySourceIdsForPurchaseOrderItem(selectedItemMaster),
        [selectedItemMaster],
    );
    const hasInvalidCompanySourceEligibility = hasInvalidEmptyCompanySourceEligibility(selectedItemMaster);
    const effectiveCompanySourceIds = useMemo(() => {
        if (!selectedItemMaster) return selectedCompanySourceIds;
        return reconcileSelectedCompanySourceIdsForPurchaseOrderItem(selectedCompanySourceIds, selectedItemMaster);
    }, [selectedItemMaster, selectedCompanySourceIds]);
    const canAddItem =
        Boolean(selectedItemMasterSku) && effectiveCompanySourceIds.length > 0 && !hasInvalidCompanySourceEligibility;
    const canClearSelection = Boolean(selectedExternalSku || selectedItemMasterSku);
    const canSelectCompanySources = Boolean(selectedItemMasterSku);

    const handleAddItem = useCallback(async () => {
        if (isAddingItem) return;
        if (!selectedItemMasterSku) return;
        const match = itemMastersBySku.get(String(selectedItemMasterSku));
        if (!match) return;
        if (hasInvalidEmptyCompanySourceEligibility(match)) return;
        if (!effectiveCompanySourceIds.length) return;

        const persistedItems = purchaseOrderDetail?.items ?? purchaseOrderDetail?.purchaseOrderItems ?? [];
        const existingRows = [
            ...(Array.isArray(persistedItems) ? persistedItems : []).filter((item) => {
                const poMapId = getPoMapId(item);
                return poMapId && !removedItemIds.has(poMapId);
            }),
            ...addedItems.filter((item) => !removedItemIds.has(String(item.id))),
        ];

        const vendorId =
            primaryDraft?.vendorId ?? primaryPurchaseOrder?.vendorID ?? primaryPurchaseOrder?.vendorId ?? null;
        const scheduledDeliveryDate =
            primaryDraft?.scheduledDeliveryDate ?? primaryPurchaseOrder?.scheduledDeliveryDate ?? '';

        setIsAddingItem(true);
        let vendorSku = '';
        try {
            if (vendorId) {
                const resp = await procurementApi.getLastPOItemFromVendor(vendorId, match.sku);
                const last = resp?.status && resp?.data?.rows?.length ? resp.data.rows[0] : null;
                vendorSku = last?.vendorSku ?? '';
            }
        } catch (e) {
            // best-effort: ignore lookup failures and leave vendorSku blank
        } finally {
            setIsAddingItem(false);
        }

        let rowSequence = nextIdRef.current;
        const newRows = effectiveCompanySourceIds.map((companySourceId) => {
            const nextRow = buildPurchaseOrderItemRowFromMaster({
                match,
                companySourceId,
                vendorSku,
                scheduledDeliveryDate,
                rowSequence,
            });
            rowSequence += 1;
            return nextRow;
        });
        nextIdRef.current = rowSequence;

        if (!itemEditStateRef.current.inputs) itemEditStateRef.current.inputs = {};
        for (const nextRow of newRows) {
            itemEditStateRef.current.inputs[nextRow.id] = {
                ...itemEditStateRef.current.inputs[nextRow.id],
                csPallet: nextRow.csPallet,
                vendorSku: nextRow.vendorSku,
                week: nextRow.week,
                year: nextRow.year,
            };
        }

        setAddedItems((prev) => [...prev, ...newRows]);

        const { itemSummaryErrors: duplicateSummaryErrors } = validateDuplicateLineItems({
            rows: [...existingRows, ...newRows],
            itemEditStateRef,
            itemMastersBySku,
            removedItemIds: new Set(),
        });
        setAddItemError(formatDuplicateLineItemLiveSummary(duplicateSummaryErrors));

        setSelectedExternalSku('');
        setSelectedItemMasterSku('');
        setSelectedCompanySourceIds([]);
    }, [
        addedItems,
        effectiveCompanySourceIds,
        isAddingItem,
        itemMastersBySku,
        primaryDraft,
        primaryPurchaseOrder,
        purchaseOrderDetail,
        removedItemIds,
        selectedItemMasterSku,
    ]);

    const handleCompanySourceIdsChange = useCallback((nextCompanySourceIds) => {
        setAddItemError('');
        setSelectedCompanySourceIds(nextCompanySourceIds);
    }, []);

    const handleRemoveNewRow = useCallback(
        (rowId) => {
            const normalizedId = String(rowId);
            setAddedItems((prev) => prev.filter((item) => String(item.id) !== normalizedId));
            setRemovedItemIds((prev) => {
                const next = new Set(prev);
                next.delete(normalizedId);
                return next;
            });

            if (itemEditStateRef?.current?.inputs) delete itemEditStateRef.current.inputs[normalizedId];
            if (itemEditStateRef?.current?.selects) delete itemEditStateRef.current.selects[normalizedId];
            if (itemEditStateRef?.current?.multi) delete itemEditStateRef.current.multi[normalizedId];
        },
        [setAddedItems, setRemovedItemIds],
    );

    const handleClearSelection = useCallback(() => {
        setSelectedExternalSku('');
        setSelectedItemMasterSku('');
        setSelectedCompanySourceIds([]);
        setAddItemError('');
    }, []);

    const handleItemMasterSkuChange = useCallback(
        (sku) => {
            setSelectedItemMasterSku(sku);
            setAddItemError('');
            const match = sku ? itemMastersBySku.get(String(sku)) : undefined;
            setSelectedCompanySourceIds(getDefaultCompanySourceIdsForPurchaseOrderItem(match));
        },
        [itemMastersBySku],
    );

    const addedItemIds = useMemo(() => new Set(addedItems.map((item) => String(item.id))), [addedItems]);

    // Only persisted items (not client-generated `new-*` rows) require a remove reason code / API removal call.
    const hasRemovals = useMemo(
        () => Array.from(removedItemIds).some((id) => !addedItemIds.has(String(id))),
        [addedItemIds, removedItemIds],
    );

    const hasAdditions = useMemo(
        () => addedItems.some((item) => !removedItemIds.has(String(item.id))),
        [addedItems, removedItemIds],
    );

    const hasQtyEdits = useMemo(() => {
        const items = purchaseOrderDetail?.items ?? purchaseOrderDetail?.purchaseOrderItems ?? [];
        if (!Array.isArray(items) || items.length === 0) return false;

        return items.some((item) => {
            const poMapId = getPoMapId(item);
            if (!poMapId) return false;
            if (removedItemIds.has(poMapId)) return false;

            const qtyValue = getItemEditInputValue(itemEditStateRef, poMapId, 'qty');
            if (qtyValue === undefined) return false;

            const diff = buildPurchaseOrderMapItemDiff({ item, uiDraft: { qty: qtyValue } });
            return Object.prototype.hasOwnProperty.call(diff, 'qty');
        });
    }, [itemEditsVersion, purchaseOrderDetail, removedItemIds]);

    const validation = useMemo(
        () =>
            validatePurchaseOrderEdit({
                addedItems,
                addItemReasonCode,
                itemEditStateRef,
                itemMastersBySku,
                primaryDraft,
                primaryPurchaseOrder,
                purchaseOrderDetail,
                qtyReasonCode,
                removedItemIds,
                removeReasonCode,
            }),
        [
            addItemReasonCode,
            addedItems,
            itemEditsVersion,
            itemEditStateRef,
            itemMastersBySku,
            primaryDraft,
            primaryPurchaseOrder,
            purchaseOrderDetail,
            qtyReasonCode,
            removeReasonCode,
            removedItemIds,
        ],
    );

    useEffect(() => {
        if (!isEditing) return;
        if (!hasRemovals && removeReasonCode) {
            setRemoveReasonCode('');
        }
    }, [hasRemovals, isEditing, removeReasonCode]);

    const handleSaveAll = useCallback(async () => {
        if (!purchaseOrderId) {
            toast.error('No purchase order ID available');
            return;
        }

        if (!primaryPurchaseOrder) {
            toast.error('Purchase order data not available yet');
            return;
        }

        if (!purchaseOrderDetail) {
            toast.error('Purchase order items are still loading');
            return;
        }

        setHasAttemptedSave(true);
        if (!validation.isValid) {
            return;
        }

        const initialPrimary = buildPurchaseOrderPrimaryDraft(primaryPurchaseOrder) ?? {};
        const currentPrimary = primaryDraft ?? initialPrimary;
        const primaryPayload = buildPurchaseOrderPrimaryUpdatePayload({ initialPrimary, currentPrimary });
        const hasPrimaryUpdates = Object.keys(primaryPayload).length > 0;

        const { itemUpdateEntries, newItemsToAdd, removalIds } = validation;

        if (
            !hasPrimaryUpdates &&
            itemUpdateEntries.length === 0 &&
            removalIds.length === 0 &&
            newItemsToAdd.length === 0
        ) {
            toast.message('No changes to save');
            return;
        }

        setIsSaving(true);
        try {
            if (hasPrimaryUpdates) {
                const resp = await procurementApi.updatePurchaseOrder(purchaseOrderId, primaryPayload);
                if (!resp?.status) {
                    throw new Error(resp?.msg ?? 'Failed to update purchase order');
                }
            }

            if (itemUpdateEntries.length > 0) {
                const itemResults = await Promise.all(
                    itemUpdateEntries.map(async ([poMapId, updates]) => {
                        const updatesWithReasonCode = Object.prototype.hasOwnProperty.call(updates, 'qty')
                            ? { ...updates, reasonCode: qtyReasonCode }
                            : updates;
                        if (Object.keys(updatesWithReasonCode).length === 0) return { status: true };
                        return procurementApi.updatePurchaseOrderMap(purchaseOrderId, poMapId, updatesWithReasonCode);
                    }),
                );

                const failed = itemResults.find((r) => r?.status === false);
                if (failed) {
                    throw new Error(failed?.msg ?? 'Failed to update one or more items');
                }
            }

            if (removalIds.length > 0) {
                const removeResults = await Promise.all(
                    removalIds.map(async (poMapId) =>
                        procurementApi.removeItemFromPO(purchaseOrderId, poMapId, removeReasonCode),
                    ),
                );
                const failed = removeResults.find((r) => r?.status === false);
                if (failed) {
                    throw new Error(failed?.msg ?? 'Failed to remove one or more items');
                }
            }

            // Add new items
            if (newItemsToAdd.length > 0) {
                const addPayload = {
                    items: newItemsToAdd.map((item) =>
                        buildPurchaseOrderAddItemPayloadRow({
                            item,
                            itemEditStateRef,
                            reasonCode: addItemReasonCode,
                        }),
                    ),
                };
                const resp = await procurementApi.addItemToPurchaseOrder(purchaseOrderId, addPayload);
                if (!resp?.status) {
                    throw new Error(resp?.msg ?? 'Failed to add new items');
                }
            }

            toast.success('Purchase Order updated successfully');

            await queryClient.invalidateQueries({ queryKey: purchaseOrderQueryKeys.detail(purchaseOrderId) });
            await queryClient.invalidateQueries({
                queryKey: purchaseOrderQueryKeys.searchPaginated({ poNumber: purchaseOrderId }),
            });
            await queryClient.invalidateQueries({ queryKey: purchaseOrderQueryKeys.all });

            setIsEditing(false);
            setHasAttemptedSave(false);
            setPrimaryDraft(null);
            setRemovedItemIds(new Set());
            setQtyReasonCode('');
            setRemoveReasonCode('');
            setAddedItems([]);
            setSelectedExternalSku('');
            setSelectedItemMasterSku('');
            setSelectedCompanySourceIds([]);
            setAddItemReasonCode('');
            itemEditStateRef.current = { inputs: {}, selects: {}, multi: {} };
        } catch (err) {
            toast.error(err?.message ?? 'Error saving purchase order changes');
        } finally {
            setIsSaving(false);
        }
    }, [
        addedItems,
        addItemReasonCode,
        hasRemovals,
        validation,
        primaryDraft,
        purchaseOrderDetail,
        purchaseOrderId,
        qtyReasonCode,
        queryClient,
        removeReasonCode,
        removedItemIds,
        primaryPurchaseOrder,
    ]);

    const hasValidationErrors = hasAttemptedSave && !validation.isValid;

    // TODO: implement advanced actions menu
    const handleOpenAdvancedActions = useCallback(() => {}, []);

    // Show loading state while fetching purchase order data (check both isLoading and isFetching)
    if (isLoading || isFetching) {
        return (
            <div className="flex min-h-[400px] items-center justify-center rounded-md border border-border bg-card px-4 py-12 text-center">
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Loading purchase order…</span>
                </div>
            </div>
        );
    }

    // Show error state if fetch failed
    if (isError) {
        const errorMessage = error?.message || 'Failed to load purchase order';
        return (
            <div className="flex min-h-[400px] items-center justify-center rounded-md border border-border bg-card px-4 py-12 text-center">
                <div className="flex flex-col items-center gap-4 max-w-md">
                    <AlertCircle className="h-8 w-8 text-destructive" />
                    <div className="flex flex-col items-center gap-2">
                        <span className="text-sm font-medium text-foreground">Unable to load purchase order</span>
                        <span className="text-sm text-muted-foreground">{errorMessage}</span>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-2 mt-2">
                        <Button variant="outline" onClick={() => refetch()}>
                            Retry
                        </Button>
                        <Button variant="outline" onClick={() => history.push(backHref)}>
                            Back to POs
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    const header = (
        <ViewEntityHeader
            backHref={backHref}
            backLabel="Back to POs"
            entityIdentificationComponent={
                <PurchaseOrderHeaderContent
                    purchaseOrder={primaryPurchaseOrder}
                    purchaseOrderId={purchaseOrderId}
                    onEdit={isEditing ? undefined : handleEditPurchaseOrder}
                    isEditing={isEditing}
                    isSaving={isSaving}
                    onCancel={handleCancelEditPurchaseOrder}
                    onCancelPurchaseOrder={handleOpenCancelPurchaseOrder}
                    onDuplicate={handleDuplicatePurchaseOrder}
                    onDownloadCsv={handleDownloadCsv}
                    onDownloadPdf={handleDownloadPdf}
                    onDownloadShipList={handleDownloadShipList}
                    onSendToVendor={handleSendToVendor}
                    isSendToVendorDisabled={isSendToVendorDisabled}
                    onSave={handleSaveAll}
                    hasValidationErrors={hasValidationErrors}
                    onAdvancedActions={handleOpenAdvancedActions}
                />
            }
        />
    );

    const primaryInformationCard = isEditing ? (
        <PurchaseOrderEditPrimaryCard
            purchaseOrder={primaryPurchaseOrder}
            draft={primaryDraft ?? buildPurchaseOrderPrimaryDraft(primaryPurchaseOrder)}
            onDraftChange={setPrimaryDraft}
            primaryErrors={validation.primaryErrors}
            primaryFieldErrors={validation.primaryFieldErrors}
            showValidation={hasAttemptedSave}
        />
    ) : (
        <PurchaseOrderPrimaryInfoCard purchaseOrder={primaryPurchaseOrder} />
    );
    const itemInformationCard = (
        <PurchaseOrderItemInfoCard
            purchaseOrderId={purchaseOrderId}
            isEditing={isEditing}
            purchaseOrder={purchaseOrderDetail ?? null}
            countries={countries}
            itemEditStateRef={itemEditStateRef}
            removedItemIds={removedItemIds}
            setRemovedItemIds={setRemovedItemIds}
            onItemEditsChange={handleItemEditsChange}
            qtyReasonCode={qtyReasonCode}
            setQtyReasonCode={setQtyReasonCode}
            removeReasonCode={removeReasonCode}
            setRemoveReasonCode={setRemoveReasonCode}
            hasRemovals={hasRemovals}
            hasQtyEdits={hasQtyEdits}
            // Props for adding items in edit mode
            addedItems={addedItems}
            selectedExternalSku={selectedExternalSku}
            selectedItemMasterSku={selectedItemMasterSku}
            onExternalSkuChange={setSelectedExternalSku}
            onItemMasterSkuChange={handleItemMasterSkuChange}
            effectiveCompanySourceIds={effectiveCompanySourceIds}
            onCompanySourceIdsChange={handleCompanySourceIdsChange}
            addItemError={addItemError}
            eligibleSourceIds={selectedItemMaster ? eligibleSourceIds : undefined}
            hasInvalidCompanySourceEligibility={hasInvalidCompanySourceEligibility}
            canSelectCompanySources={canSelectCompanySources}
            isAddingItem={isAddingItem}
            onAddItem={handleAddItem}
            onClearSelection={handleClearSelection}
            canAddItem={canAddItem}
            canClearSelection={canClearSelection}
            addItemReasonCode={addItemReasonCode}
            setAddItemReasonCode={setAddItemReasonCode}
            hasAdditions={hasAdditions}
            onRemoveNewRow={handleRemoveNewRow}
            itemErrors={validation.itemErrors}
            itemSummaryErrors={validation.itemSummaryErrors}
            showValidation={hasAttemptedSave}
        />
    );

    return (
        <>
            <ViewEntityPage
                header={header}
                primaryInformationCard={primaryInformationCard}
                additionalInformationCards={itemInformationCard}
            />
            <ReasonCodeDialog
                open={cancelDialog.isOpen}
                title="Select A Reason Code and Confirm"
                onCancel={cancelDialog.close}
                onConfirm={handleConfirmCancelPurchaseOrder}
                isSubmitting={cancelState.isSubmitting}
            />
        </>
    );
}

ViewPurchaseOrderEntity.displayName = 'ViewPurchaseOrderEntity';
